import React, { useEffect, useState } from 'react';
import {
    Box, Button, CircularProgress, Divider, IconButton,
    TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getSpotComments, addSpotComment, deleteSpotComment } from 'store/slices/spots';


interface Props {
    idSpot: string;
}

const SpotComments = ({ idSpot }: Props) => {
    const dispatch = useAppDispatch();
    const auth = useAppSelector((state) => state.auth);
    const { comments, commentsNextCursor, loadingComments, submittingComment } = useAppSelector((state) => state.spots);
    const [text, setText] = useState('');
    const isLoggedIn = !!auth.jwt;
    const currentUserId = auth.tokenData?.data?.idUser;

    useEffect(() => {
        void dispatch(getSpotComments({ idSpot }));
    }, [idSpot]);

    const handleSubmit = () => {
        if (!text.trim()) return;
        void dispatch(addSpotComment({ idSpot, text: text.trim() })).then(() => setText(''));
    };

    const handleDelete = (commentId: string) => {
        void dispatch(deleteSpotComment({ idSpot, commentId }));
    };

    const handleLoadMore = () => {
        void dispatch(getSpotComments({ idSpot, cursor: commentsNextCursor }));
    };

    return (
        <Box mt={2}>
            <Typography variant="h6" mb={1}>
                Comments
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {isLoggedIn && (
                <Box display="flex" gap={1} mb={2} alignItems="flex-start">
                    <TextField
                        fullWidth
                        size="small"
                        multiline
                        maxRows={4}
                        placeholder="Add a comment..."
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        inputProps={{ maxLength: 1000 }}
                    />
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSubmit}
                        disabled={submittingComment || !text.trim()}
                        sx={{ whiteSpace: 'nowrap', mt: 0.5 }}
                    >
                        {submittingComment ? <CircularProgress size={16} /> : 'Post'}
                    </Button>
                </Box>
            )}

            {comments.length === 0 && !loadingComments && (
                <Typography variant="body2" color="text.secondary">
                    No comments yet. {isLoggedIn ? 'Be the first!' : 'Log in to comment.'}
                </Typography>
            )}

            {/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */}
            {comments.map((comment) => (
                <Box key={comment.commentId} mb={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {new Date(comment.dateAdded).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                                {comment.text}
                            </Typography>
                        </Box>
                        {comment.idUser === currentUserId && (
                            <Tooltip title="Delete comment">
                                <IconButton size="small" onClick={() => handleDelete(comment.commentId)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Divider sx={{ mt: 1.5 }} />
                </Box>
            ))}
            {/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */}

            {loadingComments && (
                <Box display="flex" justifyContent="center" py={1}>
                    <CircularProgress size={20} />
                </Box>
            )}

            {commentsNextCursor && !loadingComments && (
                <Button size="small" onClick={handleLoadMore}>
                    Load more comments
                </Button>
            )}
        </Box>
    );
};

export default SpotComments;
